import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  SquaresFour, Buildings, SignOut, List, X, HardHat,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, module: "dashboard" },
  { to: "/projects", label: "Projects", icon: Buildings, module: "project" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const modules = user?.modules || [];

  const visibleNav = NAV.filter((n) => modules.includes(n.module) || n.module === "dashboard");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-screen w-64 bg-[hsl(var(--blueprint))] text-white flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
          <div className="h-8 w-8 bg-primary flex items-center justify-center">
            <HardHat size={20} weight="fill" className="text-white" />
          </div>
          <div className="leading-none">
            <div className="font-heading font-black text-lg tracking-tighter">CONSTRUCT<span className="text-primary">HUB</span></div>
            <div className="overline text-white/50 !text-[0.6rem]">Project Control</div>
          </div>
        </div>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {visibleNav.map((item) => {
            const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-150 ${
                  active ? "bg-primary text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-1">
            <div className="text-sm font-medium truncate">{user?.username}</div>
            <div className="overline text-white/50 !text-[0.6rem] truncate">{(user?.role || "").replace(/_/g, " ")}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-white/70 hover:bg-primary hover:text-white transition-colors duration-150"
          >
            <SignOut size={20} />
            Sign Out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-20 flex items-center px-4 lg:px-8 gap-3">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
          <div className="flex items-center gap-2 text-sm">
            <span className="overline text-muted-foreground">Project Control</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 border border-border text-muted-foreground">
              {user?.company_name || (user?.role || "").replace(/_/g, " ")}
            </span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
